import { useAtomValue, useStore } from "jotai";
import {
  canvasAtom,
  edgesAtom,
  nodesAtom,
  zslAtom,
  isTourableNode,
  isDraftModeIncludingTemplatesAtom,
} from "@zapier/canvas-state";
import {
  CopilotProvider as CopilotProviderAiToolkit,
  useConversationId,
  useCopilotRegistry,
} from "@zapier/ai-toolkit/client";
import { z } from "zod";
import { Tool } from "ai";
import { getCanvasCopilotRegistry } from "../../utils/canvasCopilotRegistry";
import network_request_client_tool from "@zapier/copilot-tools/src/tools/network_request";
import { edit_canvas_client_tool } from "@zapier/copilot-tools/src/tools/edit_canvas";
import { manage_todos_client_tool } from "@zapier/copilot-tools/src/tools/manage_todos";
import { search_tool_client_tool } from "@zapier/copilot-tools/src/tools/search_tool";
import { search_recipes_tool_client_tool } from "@zapier/copilot-tools/src/tools/search_recipes_tool";
// Constants for tour description character limits
const DESCRIPTION_MIN_CHARS = 100;
const DESCRIPTION_MAX_CHARS = 144;

export const tourStepSchema = z.object({
  header: z.string(),
  description: z.string().min(DESCRIPTION_MIN_CHARS).max(DESCRIPTION_MAX_CHARS),
  parentId: z.string(),
  stepNumber: z.number().int().min(0),
});

export type ToolWithInclude = Tool & {
  shouldInclude?: () => boolean;
};

/**
 * CanvasCopilotHydrator is placed as a child of CanvasHydrator to ensure immediate access to:
 * - zsl data
 * - canvas metadata
 * - node data
 * - edge data
 *
 * This placement prevents any data transition states (like placeholder to real data)
 * and ensures the copilot has all required data from initial render.
 */
export function CopilotProvider({ children }: { children: React.ReactNode }) {
  const store = useStore();
  const nodes = useAtomValue(nodesAtom);
  const edges = useAtomValue(edgesAtom);
  const zsl = useAtomValue(zslAtom);
  const canvas = useAtomValue(canvasAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  // Build mode suggestions setup (includes field mapping and other categories)
  const { registry } = useCopilotRegistry(getCanvasCopilotRegistry);

  const shouldShowBuildModeSuggestions = (): boolean => {
    return !isDraftModeIncludingTemplates;
  };

  // Calculate filtered nodes for tour description
  const filteredNodesForTour = nodes.filter(isTourableNode);

  // Smart function to determine if we should use filtered nodes
  const getNodesForContext = () => {
    const hasAnnotationNodes = nodes.some((node) => node.type === "annotation");

    if (hasAnnotationNodes) {
      // When annotation nodes exist, we're in a tour context
      return filteredNodesForTour;
    }

    // No annotation nodes = no tour context, use all nodes
    return nodes;
  };

  const canvasTools = [
    network_request_client_tool,
    edit_canvas_client_tool(store.get, store.set),
    manage_todos_client_tool(store.get, store.set),
    search_tool_client_tool,
    // search_templates_tool_client_tool,
    search_recipes_tool_client_tool,
  ];

  const conversationId = useConversationId();

  return (
    <CopilotProviderAiToolkit
      api="/api/canvasui/chat/buildcompletions"
      clientTools={canvasTools}
      copilotId={"canvas"}
      registry={registry!}
      initialConversationId={conversationId}
      maxSteps={25}
      // Use Claude 4 Sonnet for field mapping tools, default GPT-4.1 for other tools
      headers={
        shouldShowBuildModeSuggestions()
          ? { "X-Model": "claude4sonnet" }
          : undefined
      }
      body={{
        additionalData: {
          zsl: zsl || {},
          canvas: canvas || {},
          nodes: getNodesForContext(),
          edges,
        },
      }}
      onError={(error: any) => {
        console.error("Canvas copilot error:", error);
      }}
    >
      {children}
    </CopilotProviderAiToolkit>
  );
}
